'use client';

import Link from 'next/link';
import { ShoppingCart, Heart, Check, Phone } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import useSepetStore from '@/store/sepet.store';
import { useState, useRef } from 'react';
import { API_URL } from '@/lib/config';
import { useSiteAyarlari } from '@/hooks/useSiteAyarlari';
import useSiteStore from '@/store/site.store';
import toast from 'react-hot-toast';
import SepeteEkleModal from '@/components/product/SepeteEkleModal';

const RESIM_BASE = API_URL.replace('/api', '');

interface Varyasyon {
  id: number;
  varyasyon_ad: string;
  satis_fiyati: number;
  piyasa_fiyati: number;
  stok: number;
  aktif?: boolean;
  konfigurasyon_json?: string | null;
  urun_kdv?: number;
}

interface ProductCardProps {
  id: number;
  urun_ad: string;
  marka?: string;
  resim?: string;
  resim_alt?: string;
  resimler?: { dosya_yolu: string; alt_metin?: string | null }[];
  varyasyonlar?: Varyasyon[];
  kategori?: string;
  urun_kdv?: number;
  href?: string;
}

export default function ProductCard({
  id,
  urun_ad,
  marka,
  resim,
  resim_alt,
  resimler = [],
  varyasyonlar = [],
  kategori,
  urun_kdv = 20,
  href,
}: ProductCardProps) {
  const { ekle } = useSepetStore();
  const [eklendi, setEklendi] = useState(false);
  const [aktifResim, setAktifResim] = useState(0);
  const [hataliResimler, setHataliResimler] = useState<Set<number>>(new Set());
  const [modalAcik, setModalAcik] = useState(false);
  const touchStartX = useRef<number>(0);
  const siteAyarlari = useSiteAyarlari();
  const satis_kapali = useSiteStore((state) => state.satis_kapali);

  const kdvDahilGoster = (siteAyarlari?.fiyat_gosterim_tipi ?? 'kdv_dahil') === 'kdv_dahil';
  const kdvOrani = urun_kdv / 100;

  const aktifVaryasyonlar = varyasyonlar.filter(v => v.aktif !== false);
  const ilkVaryasyon = aktifVaryasyonlar[0] ?? varyasyonlar[0];
  const fiyatVar = ilkVaryasyon && Number(ilkVaryasyon.satis_fiyati) > 0;
  const cokVaryasyon = varyasyonlar.filter(v => v.aktif !== false).length > 1;

  const gosterimFiyati = fiyatVar
    ? kdvDahilGoster
      ? Math.round(Number(ilkVaryasyon.satis_fiyati) * (1 + kdvOrani))
      : Number(ilkVaryasyon.satis_fiyati)
    : 0;

  const piyasaFiyatiGosterim = fiyatVar && ilkVaryasyon.piyasa_fiyati > 0
    ? kdvDahilGoster
      ? Math.round(Number(ilkVaryasyon.piyasa_fiyati) * (1 + kdvOrani))
      : Number(ilkVaryasyon.piyasa_fiyati)
    : 0;

  const indirimVar = fiyatVar && piyasaFiyatiGosterim > gosterimFiyati;
  const indirimOrani = indirimVar
    ? Math.round(((piyasaFiyatiGosterim - gosterimFiyati) / piyasaFiyatiGosterim) * 100)
    : 0;

  const tumResimler = resimler.length > 0
    ? resimler
    : resim ? [{ dosya_yolu: resim, alt_metin: resim_alt }] : [];

  const gecerliResimler = tumResimler.filter((_, i) => !hataliResimler.has(i));
  const aktifResimYolu = gecerliResimler[aktifResim]?.dosya_yolu;
  const stokBitti = ilkVaryasyon?.stok === 0;

  const sepeteEkle = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!ilkVaryasyon || !fiyatVar) return;

    // Birden fazla aktif varyasyon varsa modal aç
    if (cokVaryasyon) {
      setModalAcik(true);
      return;
    }

    // Tek varyasyon → direkt ekle
    ekle(ilkVaryasyon.id, 1);
    setEklendi(true);
    toast.success('Sepete eklendi!', { duration: 2000 });
    setTimeout(() => setEklendi(false), 3000);
  };

  const touchBaslat = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const touchBirak = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 30) {
      if (diff > 0) setAktifResim(prev => (prev + 1) % gecerliResimler.length);
      else setAktifResim(prev => (prev - 1 + gecerliResimler.length) % gecerliResimler.length);
    }
  };

  return (
    <>
      <Link href={href ?? `/urunler/${id}`} className="group block">
        <div className="bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-lg)] transition-all duration-[var(--transition-normal)] overflow-hidden h-full flex flex-col">

          {/* Resim */}
          <div
            className="relative aspect-square bg-[var(--color-gray-100)] overflow-hidden"
            onTouchStart={touchBaslat}
            onTouchEnd={touchBirak}
          >
            {aktifResimYolu ? (
              <img
                src={`${RESIM_BASE}${aktifResimYolu}`}
                alt={gecerliResimler[aktifResim]?.alt_metin || urun_ad}
                className={`w-full h-full object-cover transition-transform duration-[var(--transition-slow)] ${stokBitti ? '' : 'group-hover:scale-105'}`}
                onError={() => {
                  setHataliResimler(prev => new Set([...prev, tumResimler.findIndex(r => r.dosya_yolu === aktifResimYolu)]));
                  if (aktifResim >= gecerliResimler.length - 1) setAktifResim(0);
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-[var(--color-gray-400)] text-sm">Resim Yok</span>
              </div>
            )}

            {indirimVar && (
              <div className="absolute top-2 left-2">
                <Badge variant="error" size="sm">-%{indirimOrani}</Badge>
              </div>
            )}

            {stokBitti && (
              <div className="absolute bottom-2 left-2">
                <Badge variant="gray">Stok Tükendi</Badge>
              </div>
            )}

            <button
              className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
              onClick={(e) => e.preventDefault()}
            >
              <Heart className="w-4 h-4" />
            </button>

            {gecerliResimler.length > 1 && (
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                {gecerliResimler.map((_, i) => (
                  <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === aktifResim ? 'bg-white' : 'bg-white/50'}`} />
                ))}
              </div>
            )}
          </div>

          {/* Bilgi */}
          <div className="p-3 flex flex-col flex-1">
            {marka && (
              <p className="text-xs text-[var(--color-gray-400)] mb-1 uppercase tracking-wide">{marka}</p>
            )}
            <h3 className="text-sm font-medium text-[var(--color-gray-800)] line-clamp-2 mb-2 flex-1">
              {urun_ad}
            </h3>

            {ilkVaryasyon && (
              <div className="flex items-center justify-between gap-2 mt-auto">
                <div className="min-w-0">
                  {fiyatVar ? (
                    <>
                      <p className="text-[var(--color-primary)] font-bold text-base">
                        {gosterimFiyati.toLocaleString('tr-TR')} ₺
                        {cokVaryasyon && <span className="text-xs font-normal text-[var(--color-gray-400)] ml-1">den</span>}
                      </p>
                      <p className="text-[var(--color-gray-400)] text-xs mt-0.5">
                        {kdvDahilGoster ? 'KDV dahil' : 'KDV hariç'}
                      </p>
                      {indirimVar && (
                        <p className="text-[var(--color-gray-400)] text-xs line-through">
                          {piyasaFiyatiGosterim.toLocaleString('tr-TR')} ₺
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-[var(--color-gray-500)] text-sm font-medium flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5" /> Fiyat Sorunuz
                    </p>
                  )}
                </div>

                {fiyatVar && !stokBitti && !satis_kapali && (
                  <button
                    onClick={sepeteEkle}
                    className={`flex-shrink-0 p-2 rounded-[var(--radius-md)] transition-all duration-[var(--transition-fast)] ${
                      eklendi
                        ? 'bg-[var(--color-success)] text-white'
                        : 'bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-dark)]'
                    }`}
                  >
                    {eklendi ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </Link>

      {/* Sepete Ekle Modal */}
      {modalAcik && (
        <SepeteEkleModal
          acik={modalAcik}
          onKapat={() => setModalAcik(false)}
          urun_id={id}
          urun_ad={urun_ad}
          urun_kdv={urun_kdv}
          resimler={resimler.length > 0 ? resimler : resim ? [{ dosya_yolu: resim, alt_metin: resim_alt }] : []}
          varyasyonlar={varyasyonlar}
        />
      )}
    </>
  );
}
